"use client";
import React, { useRef } from "react";
import { useTranslations } from "next-intl";
import { motion, useInView } from "motion/react";
import AnimatedCard from "@/components/ui/animated-card";
import BorderWrapper from "@/components/ui/border-wrapper";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/ui/carousel';

const shapeVariants = {
  visible: {
    scale: [0.9, 1, 0.9] as number[],
    transition: { duration: 5, ease: "easeInOut", repeat: Infinity },
  },
} as const;

const containerVariants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08 } },
} as const;

const itemVariants = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
} as const;

const IntegrationsWhat = () => {
  const t = useTranslations("integrations");

  const integrations = [
    { number: "01", title: t("what.items.0.title"), description: t("what.items.0.description") },
    { number: "02", title: t("what.items.1.title"), description: t("what.items.1.description") },
    { number: "03", title: t("what.items.2.title"), description: t("what.items.2.description") },
    { number: "04", title: t("what.items.3.title"), description: t("what.items.3.description") },
    { number: "05", title: t("what.items.4.title"), description: t("what.items.4.description") },
    { number: "06", title: t("what.items.5.title"), description: t("what.items.5.description") },
    { number: "07", title: t("what.items.6.title"), description: t("what.items.6.description") },
  ];
  const sectionRef = useRef<HTMLElement>(null);
  const isInView = useInView(sectionRef, { once: true, margin: "0px 0px -80px 0px" });

  return (
    <section
      id="what-we-integrate"
      ref={sectionRef}
      className="section pt-[60px] lg:pt-[140px] pb-[60px] lg:pb-[140px] relative"
    >
      <div
        className="absolute left-[0%] top-[10%] w-[600px] h-[600px] bg-[linear-gradient(180deg,_rgba(135,82,250,0.08)_0%,_rgba(34,12,81,0)_100%)] rounded-full blur-[120px] pointer-events-none animate-shape-pulse"
        />
      <div
        className="absolute right-[-5%] bottom-[20%] w-[500px] h-[500px] bg-[linear-gradient(180deg,_rgba(98,42,173,0.1)_0%,_rgba(34,12,81,0)_100%)] rounded-full blur-[100px] pointer-events-none animate-shape-pulse"
        />

      <div className="container relative z-10">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "show" : "hidden"}
        >
          <motion.div className="text-center mb-[50px] lg:mb-[70px]" variants={itemVariants}>
            <h2 className="text-[28px] md:text-[40px] lg:text-[48px] font-semibold leading-[1.1] mb-[16px]">
              {t("what.sectionTitle")}
            </h2>
            <p className="text-[15px] lg:text-[16px] font-light leading-[1.7] text-foreground/90 max-w-[500px] mx-auto">
              {t("what.sectionDescription")}
            </p>
          </motion.div>

          {/* Top 4: MOBILE CAROUSEL */}
          <div className="md:hidden mb-[14px]">
            <Carousel aria-label={t("what.sectionTitle")} opts={{ align: "center", loop: false }} className="w-full [&>div]:!overflow-visible">
              <CarouselContent className="-ml-2">
                {integrations.slice(0, 4).map((item, i) => (
                  <CarouselItem key={i} className="pl-2 basis-[85%]">
                    <AnimatedCard
                      className="rounded-[18px] lg:rounded-[22px] h-full"
                      circleClassName="w-[120px] h-[120px] blur-[70px]"
                      animationDelay={i * 0.07}
                    >
                      <BorderWrapper
                        wrapperClass="rounded-[18px] lg:rounded-[22px] h-full"
                        className="rounded-[18px] lg:rounded-[22px] h-full bg-gradient-to-b from-[#121124] to-[#0e0821] p-[28px] lg:p-[36px] relative overflow-hidden"
                      >
                        <div
                          className="absolute top-0 right-0 w-[120px] h-[120px] bg-theme/10 rounded-full blur-[50px] pointer-events-none animate-shape-pulse"
                          />
                        <div className="w-[32px] h-[3px] rounded-full bg-gradient-to-r from-theme to-[#622aad] mb-[18px]" />
                        <h3 className="text-[17px] lg:text-[19px] font-semibold leading-[1.3] mb-[12px]">
                          {item.title}
                        </h3>
                        <p className="text-[15px] lg:text-[16px] font-light leading-[1.7] text-foreground/90">
                          {item.description}
                        </p>
                      </BorderWrapper>
                    </AnimatedCard>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <div className="flex justify-center items-center mt-[30px] gap-[15px]">
                <CarouselPrevious className="static translate-x-0 translate-y-0 w-[50px] h-[50px] rounded-full bg-[#4a3290] text-white disabled:bg-[#15092c] cursor-pointer border-white/5 focus:bg-theme focus:text-white" />
                <CarouselNext className="static translate-x-0 translate-y-0 w-[50px] h-[50px] rounded-full bg-[#4a3290] text-white disabled:bg-[#15092c] cursor-pointer border-white/5 focus:bg-theme focus:text-white" />
              </div>
            </Carousel>
          </div>
          {/* Top 4: DESKTOP GRID */}
          <div className="hidden md:grid md:grid-cols-2 gap-[14px] mb-[14px]">
            {integrations.slice(0, 4).map((item, i) => (
              <motion.div key={i} variants={itemVariants}>
                <AnimatedCard
                  className="rounded-[18px] lg:rounded-[22px] h-full"
                  circleClassName="w-[120px] h-[120px] blur-[70px]"
                  animationDelay={i * 0.07}
                >
                  <BorderWrapper
                    wrapperClass="rounded-[18px] lg:rounded-[22px] h-full"
                    className="rounded-[18px] lg:rounded-[22px] h-full bg-gradient-to-b from-[#121124] to-[#0e0821] p-[28px] lg:p-[36px] relative overflow-hidden"
                  >
                    <div
                      className="absolute top-0 right-0 w-[120px] h-[120px] bg-theme/10 rounded-full blur-[50px] pointer-events-none animate-shape-pulse"
                      />
                    <div className="w-[32px] h-[3px] rounded-full bg-gradient-to-r from-theme to-[#622aad] mb-[18px]" />
                    <h3 className="text-[17px] lg:text-[19px] font-semibold leading-[1.3] mb-[12px]">
                      {item.title}
                    </h3>
                    <p className="text-[15px] lg:text-[16px] font-light leading-[1.7] text-foreground/90">
                      {item.description}
                    </p>
                  </BorderWrapper>
                </AnimatedCard>
              </motion.div>
            ))}
          </div>

        </motion.div>

          {/* Bottom 3 as horizontal rows */}
          <div className="max-w-[860px] mx-auto">
            {integrations.slice(4).map((item, i) => (
              <div
                key={i}
                className="sticky mt-[2px] lg:mt-[14px]"
                style={{ top: `${20 + i * 64}px`, zIndex: 10 + i }}
              >
                <AnimatedCard
                  className="rounded-[16px]"
                  circleClassName="w-[100px] h-[100px] blur-[60px]"
                  animationDelay={i * 0.06}
                >
                  <BorderWrapper
                    wrapperClass="rounded-[16px]"
                    className="rounded-[16px] bg-gradient-to-b from-[#121124] to-[#0e0821] relative overflow-hidden"
                  >
                    <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-gradient-to-b from-theme via-[#622aad] to-transparent" />
                    <div className="pl-[28px] pr-[24px] lg:pr-[40px] py-[22px] lg:py-[28px] grid lg:grid-cols-[1fr_1.6fr] gap-[8px] lg:gap-[60px] items-center">
                      <h3 className="text-[16px] lg:text-[18px] font-semibold leading-[1.3]">
                        {item.title}
                      </h3>
                      <p className="text-[15px] lg:text-[16px] font-light leading-[1.7] text-foreground/90">
                        {item.description}
                      </p>
                    </div>
                  </BorderWrapper>
                </AnimatedCard>
              </div>
            ))}
          </div>
      </div>
    </section>
  );
};

export default IntegrationsWhat;
